import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { Person } from '../../../api/models/Person';
import { peopleSlice } from '../../../model/peopleSlice';
import { RootState } from '../../../model/store';
import { routes } from '../../../route-list';
import { detailedToastOptions } from '../../../util/toast-utilities';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { InvitedUser } from '../../core/InviteModal';
import { RhTab } from '../../core/RhTabs';
import { PeopleScreen } from '../components/PeopleScreen';

export type CSVEntry = {
  name: string;
  email: string;
  joinDate: string;
  role: string;
};

export type PeopleProps = {
  // Inputs
  /** current tab id on the uri */
  currentTabId: string;
  /** tabs of this view */
  tabs: RhTab[];
  /** if form to invite someone is open */
  isInviteModalOpen: boolean;
  /** loading state of invite modal */
  isInviteModalLoading: boolean;
  /** the list of users to populate "people" */
  users: Person[];
  /** whether or not list of "people" is fetching */
  isLoadingUsers: boolean;
  /** csv generated data from users */
  csvData: CSVEntry[];

  // Inputs
  /** called when the user clicks the button */
  userClickedButton: () => void;
  /** called when the user clicks a tab */
  userSelectedTab: (id: string) => void;
  /** called when user wants to invite someone */
  userToggledInviteModal: () => void;
  /** called when user submits the invite form  */
  userSubmittedInvites: (users: InvitedUser[]) => void;
  /** called when user clicks the "View/Edit Details" button */
  userClickedEditPerson: (id: string) => void;
  /** called when the search field receives a [change] event */
  userUpdatedSearchTerm: (term: string) => void;
};

const filterUsersByRole = (roleId: string, users: Person[]) => {
  return roleId === 'all' ? users : users.filter(user => _.lowerCase(user.role) === _.lowerCase(roleId));
};

export const PeopleContainer = () => {
  const users = useSelector((state: RootState) => state.people.list);

  const [currentTabId, setCurrentTabId] = useState('all');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInviteModalLoading, setIsInviteModalLoading] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<Person[]>(users);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const userSearcher = new FuzzySearch(filteredUsers, ['name', 'email'], {
    sort: true,
  });

  const history = useHistory();
  const dispatch = useDispatch();
  const request = useRequest();

  const { loading: loadingUsers } = useAsyncRequest(
    useCallback(() => request<Person[]>(apiConfigs.getUsers()), [request]),
    useCallback(
      persons => {
        dispatch(peopleSlice.actions.receivedList({ persons }));
        setFilteredUsers(filterUsersByRole(currentTabId, persons));
      },
      [currentTabId, dispatch]
    )
  );

  return (
    <PeopleScreen
      csvData={users.map(user => {
        return {
          name: user.name,
          email: user.email,
          role: user.role,
          joinDate: user.created.toString(),
        };
      })}
      userUpdatedSearchTerm={(term: string) => {
        setSearchTerm(term);
      }}
      isLoadingUsers={loadingUsers}
      users={userSearcher.search(searchTerm)}
      isInviteModalOpen={isInviteModalOpen}
      isInviteModalLoading={isInviteModalLoading}
      currentTabId={currentTabId}
      tabs={[
        { id: 'all', title: 'All' },
        { id: 'studier', title: 'Studiers' },
        { id: 'event leader', title: 'Event Leaders' },
        { id: 'content creator', title: 'Content Creators' },
        { id: 'church admin', title: 'Church Admins' },
      ]}
      userClickedButton={() => {
        history.push(routes.TODO__LIST);
      }}
      userSelectedTab={tabId => {
        setCurrentTabId(tabId);
        setFilteredUsers(filterUsersByRole(tabId, users));
      }}
      userToggledInviteModal={() => setIsInviteModalOpen(prev => !prev)}
      userSubmittedInvites={async users => {
        try {
          setIsInviteModalLoading(true);
          const requests = users.map(user => request(apiConfigs.inviteUser(user)));
          await Promise.allSettled(requests).then(results => {
            // results with attached user
            const detailedResults = results.map((result, i) => ({ ...result, user: users[i] }));
            setIsInviteModalLoading(false);
            const successfulEmails = detailedResults.filter(res => res.status === 'fulfilled').map(res => res.user);
            const failedEmails = detailedResults.filter(res => res.status === 'rejected').map(res => res.user);

            if (successfulEmails.length) {
              showDetailedToast({
                title: 'Successfully sent invites to:',
                emails: successfulEmails,
                type: 'success',
              });
            }
            if (failedEmails.length) {
              showDetailedToast({
                title: 'Failed to send invites to',
                emails: failedEmails,
                type: 'error',
                footer: 'They may have been invited already.',
              });
            }
          });
        } finally {
          setIsInviteModalOpen(false);
        }
      }}
      userClickedEditPerson={id => {
        history.push(routes.PEOPLE__VIEW(id));
      }}
    />
  );
};

const showDetailedToast = (args: {
  title: string;
  emails: InvitedUser[];
  type: 'error' | 'success';
  footer?: React.ReactNode;
}) =>
  toast(
    <div>
      <div>{args.title}</div>
      {args.emails.map(i => (
        <div key={i.email}>{i.email}</div>
      ))}
      <div>{args.footer}</div>
    </div>,
    {
      ...detailedToastOptions,
      type: args.type,
    }
  );
